import React, { useState, useEffect } from 'react';
import { Card, Statistic, Table, Tag, Spin, Empty, Row, Col } from 'antd';
import {
  IndianRupee,
  TrendingUp,
  Target,
  Handshake,
  UserPlus,
} from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import useApiMethods from '../../lib/useApiMethods';
import { CRM_DATA, CRM_RECEIVABLES, TRENDS } from '../../constants/api';
import ExportButton from '../../components/common/ExportButton';
import { MetricCard, HeadlineCard, Surface } from '../../components/insights';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend
);

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const formatLakhs = (amount) => `₹${(amount / 100000).toFixed(1)}L`;

const SalesOverview = () => {
  const { fetch } = useApiMethods();
  const [data, setData] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [trends, setTrends] = useState({ time_series: [], deltas: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const [res, recRes, trendsRes] = await Promise.all([
        fetch(CRM_DATA, {}, setLoading),
        fetch(CRM_RECEIVABLES),
        fetch(TRENDS, { period: 'week' }),
      ]);
      if (res?.status !== 'fail') {
        setData(res?.data || res);
      }
      if (recRes?.status === 'success') {
        setReceivables(recRes.data);
      }
      if (trendsRes?.status === 'success') {
        setTrends(trendsRes.data || { time_series: [], deltas: {} });
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Empty description="Unable to load sales data" className="mt-20" />;
  }

  const {
    revenue_this_month = 0,
    pipeline_total = 0,
    win_rate = 0,
    deals_closed_this_week = 0,
    revenue_trend: raw_trend,
    pipeline_by_stage: raw_pipeline,
    top_deals: raw_deals,
    forecast: raw_forecast,
    new_leads: raw_new_leads,
  } = data;

  const new_leads = raw_new_leads || { today: 0, this_week: 0, this_month: 0, stage_count: 0 };

  const rec_summary = receivables?.summary || {};
  const rec_clients = receivables?.clients || [];
  const amc_pipeline = receivables?.amc_pipeline || {};

  const revenue_trend = raw_trend || [];
  const top_deals = raw_deals || [];
  const forecast = raw_forecast || {};
  const forecast_by_month = forecast.forecast_by_month || [];
  const deals_at_risk = forecast.deals_at_risk || [];
  const forecast_summary = forecast.summary || {};

  const pipeline_by_stage = Array.isArray(raw_pipeline)
    ? raw_pipeline
    : raw_pipeline ? Object.entries(raw_pipeline).map(([stage, value]) => ({ stage, value })) : [];

  // Revenue Trend Line Chart
  const revenueTrendData = {
    labels: revenue_trend.map((r) => r.month),
    datasets: [
      {
        label: 'Revenue',
        data: revenue_trend.map((r) => r.revenue),
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#6366f1',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const revenueTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` Revenue: ${formatINR(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        ticks: { callback: (val) => formatLakhs(val) },
        grid: { color: 'rgba(148,163,184,0.18)' },
      },
    },
  };

  // Pipeline by Stage horizontal Bar Chart
  const stageColors = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'];
  const pipelineChartData = {
    labels: pipeline_by_stage.map((s) => s.stage),
    datasets: [
      {
        label: 'Pipeline Value',
        data: pipeline_by_stage.map((s) => s.value),
        backgroundColor: stageColors.slice(0, pipeline_by_stage.length),
        borderRadius: 6,
        barThickness: 28,
      },
    ],
  };

  const pipelineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` Value: ${formatINR(ctx.parsed.x)}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { callback: (val) => formatLakhs(val) },
        grid: { color: 'rgba(148,163,184,0.18)' },
      },
      y: { grid: { display: false } },
    },
  };

  const stageTagColors = {
    discovery: 'blue',
    qualification: 'cyan',
    proposal: 'purple',
    negotiation: 'orange',
    closed_won: 'green',
    closed_lost: 'red',
  };

  const dealColumns = [
    {
      title: 'Deal Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (val) => formatINR(val),
      align: 'right',
      sorter: (a, b) => a.value - b.value,
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      render: (stage) => (
        <Tag color={stageTagColors[stage] || 'default'}>
          {stage?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
        </Tag>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
    },
  ];

  // Helper: sparkline + delta for a metric from trends
  const seriesFor = (key) => trends.time_series?.length
    ? trends.time_series.map((t) => t[key] || 0)
    : null;
  const deltaFor = (key) => trends.deltas?.[key]?.delta_pct ?? null;

  // Sales headline summary
  const outstandingPct = rec_summary.total_clients
    ? Math.round((rec_summary.clients_with_pending || 0) / rec_summary.total_clients * 100)
    : 0;
  const headlineState = (deals_at_risk.length > 3 || (rec_summary.amc_overdue_count || 0) > 0)
    ? 'critical'
    : (deals_at_risk.length > 0 || outstandingPct > 30)
      ? 'warning'
      : 'healthy';
  const headlineTitle = deals_at_risk.length > 0
    ? `${deals_at_risk.length} deal${deals_at_risk.length > 1 ? 's' : ''} at risk · ${formatINR(deals_at_risk.reduce((s, d) => s + (d.value || 0), 0))} exposure`
    : (rec_summary.amc_overdue_count || 0) > 0
      ? `${rec_summary.amc_overdue_count} AMC${rec_summary.amc_overdue_count > 1 ? 's' : ''} overdue — renewals needed`
      : `Pipeline healthy · ${formatLakhs(pipeline_total)} across ${top_deals.length} open deals`;

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen text-foreground">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Sales</h2>
        <p className="text-sm text-muted-foreground">Pipeline, revenue, and client health</p>
      </div>

      {/* Headline */}
      <HeadlineCard
        state={headlineState}
        title={headlineTitle}
        description={`${new_leads.this_week} new leads this week · ${formatLakhs(revenue_this_month)} revenue this month · ${win_rate}% win rate`}
        metrics={[
          { label: 'New Leads (wk)', value: new_leads.this_week, hint: `${new_leads.today} today` },
          { label: 'Revenue', value: formatLakhs(revenue_this_month) },
          { label: 'Pipeline', value: formatLakhs(pipeline_total) },
          { label: 'Outstanding', value: formatLakhs(rec_summary.outstanding_total || 0), hint: `${rec_summary.clients_with_pending || 0} clients` },
        ]}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          icon={UserPlus}
          intent="info"
          title="New Leads This Week"
          value={new_leads.this_week}
          subtitle={`${new_leads.today} today · ${new_leads.this_month} this month`}
          sparkline={seriesFor('new_leads_this_week')}
          delta={deltaFor('new_leads_this_week')}
          goodUp={true}
        />
        <MetricCard
          icon={IndianRupee}
          intent="positive"
          title="Bookings This Month"
          value={formatLakhs(revenue_this_month)}
          subtitle="Closed Won contract value"
          sparkline={seriesFor('bookings_this_month') || seriesFor('revenue_this_month')}
          delta={deltaFor('bookings_this_month') ?? deltaFor('revenue_this_month')}
          goodUp={true}
        />
        <MetricCard
          icon={IndianRupee}
          intent="info"
          title="Cash Collected"
          value={formatLakhs((data?.cash_collected_this_month) || 0)}
          subtitle={`${data?.cash_receipt_count_this_month || 0} receipts`}
          sparkline={seriesFor('cash_collected_this_month')}
          delta={deltaFor('cash_collected_this_month')}
          goodUp={true}
        />
        <MetricCard
          icon={TrendingUp}
          intent="primary"
          title="Pipeline Total"
          value={formatLakhs(pipeline_total)}
          sparkline={seriesFor('pipeline_total')}
          delta={deltaFor('pipeline_total')}
          goodUp={true}
        />
        <MetricCard
          icon={Target}
          intent={win_rate < 20 ? 'negative' : 'warning'}
          title="Win Rate"
          value={`${win_rate}%`}
          sparkline={seriesFor('win_rate')}
          delta={deltaFor('win_rate')}
          goodUp={true}
        />
        <MetricCard
          icon={Handshake}
          intent="info"
          title="Deals Closed This Week"
          value={deals_closed_this_week}
          sparkline={seriesFor('deals_closed_this_week')}
          delta={deltaFor('deals_closed_this_week')}
          goodUp={true}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Revenue Trend</h4>
          {revenue_trend.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 300 }}><Line data={revenueTrendData} options={revenueTrendOptions} /></div>
          )}
        </div>
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Pipeline by Stage</h4>
          {pipeline_by_stage.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 300 }}><Bar data={pipelineChartData} options={pipelineChartOptions} /></div>
          )}
        </div>
      </div>

      {/* Forecast */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <h4 className="font-semibold text-foreground mb-4">Revenue Forecast</h4>
        {forecast_by_month.length === 0 ? <Empty description="No forecast data" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
          <>
            <div style={{ height: 300 }}>
              <Bar
                data={{
                  labels: forecast_by_month.map((m) => m.month),
                  datasets: [
                    { label: 'Pipeline', data: forecast_by_month.map((m) => m.total_value), backgroundColor: 'rgba(99,102,241,0.2)', borderColor: '#6366f1', borderWidth: 1, borderRadius: 6 },
                    { label: 'Weighted', data: forecast_by_month.map((m) => m.weighted_value), backgroundColor: 'rgba(16,185,129,0.6)', borderColor: '#10b981', borderWidth: 1, borderRadius: 6 },
                  ],
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top', labels: { boxWidth: 10, font: { size: 11 } } } }, scales: { y: { ticks: { callback: (v) => `₹${(v/100000).toFixed(0)}L` }, grid: { color: 'rgba(148,163,184,0.15)' } }, x: { grid: { display: false } } } }}
              />
            </div>
            {forecast_summary.total_pipeline > 0 && (
              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                <div><span className="text-xs text-muted-foreground">Total Pipeline</span><div className="text-lg font-bold text-foreground">{formatINR(forecast_summary.total_pipeline)}</div></div>
                <div><span className="text-xs text-muted-foreground">Weighted Forecast</span><div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatINR(forecast_summary.total_weighted)}</div></div>
                <div><span className="text-xs text-muted-foreground">At Risk</span><div className="text-lg font-bold text-red-500">{forecast_summary.deals_at_risk_count} deals</div></div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Deals at Risk */}
      {deals_at_risk.length > 0 && (
        <div className="rounded-2xl bg-card border border-rose-200 dark:border-rose-500/30 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-rose-600 dark:text-rose-400">Deals at Risk</h4>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">{deals_at_risk.length} deals</span>
              <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                {formatINR(deals_at_risk.reduce((s, d) => s + (d.value || 0), 0))} exposure
              </span>
            </div>
          </div>
          <div className="space-y-2">
            {deals_at_risk.map((d) => (
              <div
                key={d.id}
                className="relative flex items-center gap-4 p-3 rounded-xl border border-rose-200/60 dark:border-rose-500/20 bg-gradient-to-r from-rose-50/80 via-rose-50/40 to-transparent dark:from-rose-950/40 dark:via-rose-950/20 dark:to-transparent overflow-hidden"
              >
                <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-rose-400 to-rose-600 rounded-l-xl" />
                <div className="flex-1 min-w-0 pl-1">
                  <div className="font-semibold text-foreground truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.owner} · {d.stage}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-foreground">{formatINR(d.value)}</div>
                  <span className="inline-block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200/60 dark:border-rose-500/20">
                    {d.days_overdue}d overdue
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Deals */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-foreground">Proposal & Negotiation Deals</h4>
            <span className="text-sm text-muted-foreground">{top_deals.length} deals · {formatINR(top_deals.reduce((s, d) => s + (d.value || 0), 0))} total value</span>
          </div>
          <ExportButton
            data={top_deals}
            filename="deals-proposal-negotiation"
            pdfColumns={[{title:'Deal',dataKey:'name'},{title:'Value',dataKey:'value'},{title:'Stage',dataKey:'stage'},{title:'Owner',dataKey:'owner'},{title:'Expected Close',dataKey:'expected_close'}]}
            pdfTitle="Proposal & Negotiation Deals"
          />
        </div>
        <Table
          dataSource={top_deals}
          columns={[...dealColumns, { title: 'Expected Close', dataIndex: 'expected_close', key: 'expected_close', render: (d) => d || '-' }]}
          rowKey={(record) => record.id || record.name}
          pagination={{ pageSize: 10 }}
          size="middle"
          locale={{ emptyText: <Empty description="No deals" /> }}
        />
      </div>

      {/* Client Receivables */}
      {rec_clients.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-foreground">Client Receivables</h4>
              <span className="text-sm text-muted-foreground">
                {rec_summary.clients_with_pending || 0} of {rec_summary.total_clients || 0} clients with pending · {formatINR(rec_summary.outstanding_total || 0)} outstanding
              </span>
            </div>
            <ExportButton
              data={rec_clients.map((c) => ({
                org_name: c.org_name,
                product_sold: c.product_sold,
                contract_value: c.contract_value,
                paid: c.paid,
                pending: c.pending,
                onboarding_status: c.onboarding_status,
                amc_status: c.amc?.amc_status || '-',
                amc_end_date: c.amc?.amc_end_date || '-',
              }))}
              filename="client-receivables"
              pdfColumns={[
                { title: 'Client', dataKey: 'org_name' },
                { title: 'Product', dataKey: 'product_sold' },
                { title: 'Billed', dataKey: 'contract_value' },
                { title: 'Paid', dataKey: 'paid' },
                { title: 'Pending', dataKey: 'pending' },
                { title: 'AMC Status', dataKey: 'amc_status' },
                { title: 'AMC End', dataKey: 'amc_end_date' },
              ]}
              pdfTitle="Client Receivables"
            />
          </div>
          <Table
            dataSource={rec_clients}
            columns={[
              { title: 'Client', dataIndex: 'org_name', key: 'org_name', render: (v, r) => (
                <div>
                  <div className="font-medium text-foreground">{v}</div>
                  {r.city && <div className="text-xs text-muted-foreground">{r.city}</div>}
                </div>
              )},
              { title: 'Product', dataIndex: 'product_sold', key: 'product_sold', render: (v) => v || '-' },
              { title: 'Billed', dataIndex: 'contract_value', key: 'contract_value',
                render: (v) => <span className="font-medium">{formatINR(v || 0)}</span>,
                sorter: (a, b) => (a.contract_value || 0) - (b.contract_value || 0) },
              { title: 'Paid', dataIndex: 'paid', key: 'paid',
                render: (v) => <span className="text-emerald-600 dark:text-emerald-400 font-medium">{formatINR(v || 0)}</span>,
                sorter: (a, b) => (a.paid || 0) - (b.paid || 0) },
              { title: 'Pending', dataIndex: 'pending', key: 'pending',
                render: (v) => v > 0
                  ? <span className="text-rose-600 dark:text-rose-400 font-semibold">{formatINR(v)}</span>
                  : <Tag color="green">Paid</Tag>,
                sorter: (a, b) => (a.pending || 0) - (b.pending || 0),
                defaultSortOrder: 'descend' },
              { title: 'AMC', key: 'amc', render: (_, r) => {
                if (!r.amc) return <span className="text-muted-foreground">—</span>;
                const color = r.amc.amc_status === 'Paid' ? 'green'
                  : r.amc.amc_status === 'Overdue' ? 'red' : 'orange';
                return (
                  <div>
                    <Tag color={color}>{r.amc.amc_status}</Tag>
                    {r.amc.amc_end_date && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Ends {r.amc.amc_end_date}
                        {r.amc.amc_days_to_expiry != null && r.amc.amc_days_to_expiry < 60 && (
                          <span className={r.amc.amc_days_to_expiry < 0 ? 'text-rose-600 dark:text-rose-400 ml-1' : 'text-amber-600 dark:text-amber-400 ml-1'}>
                            ({r.amc.amc_days_to_expiry < 0 ? `${Math.abs(r.amc.amc_days_to_expiry)}d overdue` : `${r.amc.amc_days_to_expiry}d`})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              }},
              { title: 'Status', dataIndex: 'onboarding_status', key: 'onboarding_status',
                render: (v) => <Tag>{v}</Tag> },
            ]}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="middle"
            locale={{ emptyText: <Empty description="No onboarded clients" /> }}
          />
        </div>
      )}

      {/* AMC Pipeline */}
      {rec_clients.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="mb-4">
            <h4 className="font-semibold text-foreground">AMC Renewal Pipeline</h4>
            <span className="text-sm text-muted-foreground">Upcoming and overdue AMC renewals</span>
          </div>
          <Row gutter={16}>
            {[
              { key: 'overdue', label: 'Overdue', color: 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300' },
              { key: 'this_month', label: 'This Month', color: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300' },
              { key: 'next_30', label: 'Next 30 Days', color: 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 text-yellow-700' },
              { key: 'next_60', label: 'Next 60 Days', color: 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 text-sky-700 dark:text-sky-300' },
              { key: 'next_90', label: 'Next 90 Days', color: 'bg-muted border-slate-200 text-slate-700' },
            ].map((bucket) => {
              const items = amc_pipeline[bucket.key] || [];
              const total = items.reduce((s, i) => s + (i.amc_pending || 0), 0);
              return (
                <Col xs={24} sm={12} md={8} lg={Math.floor(24 / 5)} key={bucket.key} style={{ marginBottom: 16 }}>
                  <div className={`rounded-xl border p-4 ${bucket.color}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{bucket.label}</div>
                    <div className="text-2xl font-bold mt-1">{items.length}</div>
                    <div className="text-xs mt-1">{formatINR(total)} pending</div>
                    {items.length > 0 && (
                      <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                        {items.slice(0, 5).map((i) => (
                          <div key={i.amc_id} className="text-xs bg-white/50 rounded px-2 py-1">
                            <div className="font-medium truncate">{i.org_name}</div>
                            <div className="opacity-70">{i.amc_end_date} · {formatINR(i.amc_pending)}</div>
                          </div>
                        ))}
                        {items.length > 5 && (
                          <div className="text-[11px] opacity-70 px-2">+{items.length - 5} more</div>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      )}
    </div>
  );
};

export default SalesOverview;
